import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { WritableDraft } from 'immer/dist/internal';
import { parseErrorMessage } from '../../../core/api/api.context';
import { SettingsMapper } from '../mappings/mappings';
import { SettingsTreeActions } from './settings-tree-action-types';
import { findAllAntecedents, generateInitialFormState } from './settings-tree-reducer-helpers';
import { EnrichedSettingsNodeDto, SettingsState, SettingsTreeDataRequestType } from './settings-tree-reducer.types';

type SettingsTreeActionHandler<T extends SettingsTreeActions[keyof SettingsTreeActions]> = (
  state: WritableDraft<SettingsState>,
  action: T
) => void;

// A Type to convert screaming snake case to camel case.
type CamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${Lowercase<T>}${Capitalize<CamelCase<U>>}`
  : Lowercase<S>;

type SettingsTreeActionHandlers = {
  [K in keyof SettingsTreeActions as CamelCase<K>]: SettingsTreeActionHandler<SettingsTreeActions[K]>;
};

const onSelectNode: SettingsTreeActionHandler<SettingsTreeActions['SELECT_NODE']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  const nodeSource = [...state.placeholderNodes, ...state.projectTree.nodes, ...state.projectChildrenTree.nodes];

  const selectedNode = nodeSource.find((n) => n.id === action.payload.nodeId);
  state.selectedNode = selectedNode;
  // Clear any enqueued node selection. Either the selected node is the enqueued node or the user has selected a different node.
  state.enqueuedNodeSelection = undefined;
  // The node being selected might not be visible in the tree (it might be a child of a collapsed node),
  // so we expand all of its ancestors. We walk the ancestry across the full node set (project tree +
  // lazily-loaded project children + placeholders), because a node's ancestors — e.g. a hub's "Hubs"
  // folder reached via a deep-link — can live in the children tree rather than the root project tree.
  const antecedents = selectedNode ? findAllAntecedents(selectedNode, { nodes: nodeSource }) : [];
  // For deep-links we also expand the selected node itself (when it has children) so the node landed
  // on from a URL reveals its contents rather than appearing as a collapsed leaf.
  const expandSelf =
    action.payload.expandNode && selectedNode && nodeSource.some((n) => n.parentId === selectedNode.id)
      ? [selectedNode.id]
      : [];
  state.expandedNodeIds = [...new Set([...state.expandedNodeIds, ...antecedents, ...expandSelf])];
  // If this is the first time this node has been selected, we need to create a form state for it.
  if (selectedNode && !formState) {
    const parentNode = selectedNode.parentId ? nodeSource.find((n) => n.id === selectedNode.parentId) : undefined;
    state.forms[action.payload.nodeId] = generateInitialFormState(
      selectedNode.id,
      selectedNode.type as KnownSettingsNodeDtoType,
      selectedNode.request.type,
      'edit',
      state.readOnly,
      selectedNode?.parentId,
      selectedNode.request.type === SettingsTreeDataRequestType.Projects ||
        selectedNode.request.type === SettingsTreeDataRequestType.ProjectChildren
        ? selectedNode.request.rootId
        : undefined,
      parentNode?.entityId,
      parentNode?.type as KnownSettingsNodeDtoType | undefined
    );
  }
};

const onEnqueueNodeSelection: SettingsTreeActionHandler<SettingsTreeActions['ENQUEUE_NODE_SELECTION']> = (
  state,
  action
) => {
  state.enqueuedNodeSelection = action.payload;
};

const onClearEnqueuedNodeSelection: SettingsTreeActionHandler<SettingsTreeActions['CLEAR_ENQUEUED_NODE_SELECTION']> = (
  state
) => {
  state.enqueuedNodeSelection = undefined;
};

const onToggleNode: SettingsTreeActionHandler<SettingsTreeActions['TOGGLE_NODE']> = (state, action) => {
  state.expandedNodeIds = action.payload.nodeIds;
};

const onCreateNode: SettingsTreeActionHandler<SettingsTreeActions['CREATE_NODE']> = (state, action) => {
  const nodeId = `${action.payload.parentNodeId}-${action.payload.nodeType}`;

  // If the node is already being created, just select it.
  const existingNode = state.placeholderNodes.find((n) => n.id === nodeId);
  if (existingNode) {
    state.selectedNode = existingNode;
    return;
  }

  const parentNode =
    state.projectTree.nodes.find((n) => n.id === action.payload.parentNodeId) ??
    state.projectChildrenTree.nodes.find((n) => n.id === action.payload.parentNodeId);

  if (!parentNode) {
    return; // If the parent node doesn't exist, we can't create a new node. This is unlikely to happen, but we handle it gracefully.
  }

  const projectId =
    parentNode.type === KnownSettingsNodeDtoType.Project
      ? parentNode.entityId
      : parentNode.request.type === SettingsTreeDataRequestType.Projects ||
        parentNode.request.type === SettingsTreeDataRequestType.ProjectChildren
      ? parentNode.request.rootId
      : undefined;

  // We use a temporary entity ID for the placeholder node. This ID will be replaced when the node is created.
  const tempEntityId = crypto.randomUUID();

  state.forms[nodeId] = generateInitialFormState(
    nodeId,
    action.payload.nodeType,
    action.payload.requestType,
    'create',
    state.readOnly,
    action.payload.parentNodeId,
    projectId,
    parentNode.entityId,
    parentNode.type as KnownSettingsNodeDtoType,
    action.payload.data
  );
  const displayLabel = SettingsMapper.NodeTypeToCreateNodeLabel(action.payload.nodeType);
  const placeHolderNode: EnrichedSettingsNodeDto = {
    id: nodeId,
    entityId: tempEntityId,
    parentEntityId: parentNode.entityId,
    parentId: action.payload.parentNodeId,
    type: action.payload.nodeType,
    displayLabel,
    isPlaceholder: true,
    // For placeholders, we use the parent node's request data
    request: parentNode.request
  };

  state.selectedNode = placeHolderNode;
  state.placeholderNodes.push(placeHolderNode);
  if (!state.expandedNodeIds.includes(action.payload.parentNodeId)) {
    state.expandedNodeIds.push(action.payload.parentNodeId);
  }
};

const onSetCurrentAction: SettingsTreeActionHandler<SettingsTreeActions['SET_CURRENT_ACTION']> = (state, action) => {
  switch (action.payload.type) {
    case 'DELETE_NODE':
      state.currentAction = {
        type: 'DELETE_NODE',
        nodeId: action.payload.action.nodeId,
        entityId: action.payload.action.entityId,
        parentNodeId: action.payload.action.parentNodeId,
        nodeType: action.payload.action.nodeType,
        childNodeIds: action.payload.action.childNodeIds,
        projectId: action.payload.action.projectId,
        requestType: action.payload.action.requestType,
        label: action.payload.action.label
      };
      break;
    case 'DELETE_RELATIONSHIP':
      state.currentAction = {
        type: 'DELETE_RELATIONSHIP',
        relatedEntityId: action.payload.relatedEntityId,
        selectedEntityId: action.payload.selectedEntityId,
        relationshipType: action.payload.relationshipType
      };
      break;
    case 'DELETE_NODE_SUCCESS':
      if (state.currentAction?.type === 'DELETE_NODE') {
        onSelectNode(state, {
          type: 'SELECT_NODE',
          payload: {
            // The only way the parent node can be undefined is if the user has deleted the root node.
            nodeId: state.currentAction.parentNodeId || '',
            isPlaceholder: false,
            requestType: state.currentAction.requestType
          }
        });
      }
      state.currentAction = {
        type: 'DELETE_NODE_SUCCESS'
      };

      break;
    case 'OPEN_REGISTRATION_PAGE':
      state.currentAction = {
        type: 'OPEN_REGISTRATION_PAGE',
        projectId: action.payload.projectId,
        registrationPageId: action.payload.registrationPageId
      };
      break;
    case 'HUB_CREATED':
      state.currentAction = {
        type: 'HUB_CREATED',
        hubId: action.payload.action.hubId,
        hubName: action.payload.action.hubName,
        managedByProjectId: action.payload.action.managedByProjectId
      };
      break;
    case 'PROJECT_CREATED':
      state.currentAction = {
        type: 'PROJECT_CREATED'
      };
      break;
    case 'ADD_CARRIER':
      state.currentAction = {
        type: 'ADD_CARRIER',
        parentProjectId: action.payload.parentProjectId,
        parentNodeId: action.payload.parentNodeId
      };
      break;
    case 'CARRIER_PROJECT_CREATED':
      state.currentAction = {
        type: 'CARRIER_PROJECT_CREATED',
        name: action.payload.name
      };
      break;
    case 'ASSIGN_NETWORKS':
      state.currentAction = {
        type: 'ASSIGN_NETWORKS',
        organisationGroupId: action.payload.organisationGroupId,
        carrierName: action.payload.carrierName
      };
      break;
    case 'ADD_REGION':
      state.currentAction = {
        type: 'ADD_REGION',
        carrierProjectId: action.payload.carrierProjectId,
        carrierProjectName: action.payload.carrierProjectName,
        parentNodeId: action.payload.parentNodeId
      };
      break;
    case 'APPLY_KIOSK_THEME_PRESET':
      state.currentAction = {
        type: 'APPLY_KIOSK_THEME_PRESET',
        nodeId: action.payload.nodeId,
        kioskId: action.payload.kioskId,
        projectId: action.payload.projectId
      };
      break;
    default:
      state.currentAction = undefined;
      break;
  }
};

const onClearCurrentAction: SettingsTreeActionHandler<SettingsTreeActions['CLEAR_CURRENT_ACTION']> = (state) => {
  state.currentAction = undefined;
};

const onRegisterForm: SettingsTreeActionHandler<SettingsTreeActions['REGISTER_FORM']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState) {
    formState.form = action.payload.defaultValues;
    formState.isRegistered = true;
  }
};

const onResetForm: SettingsTreeActionHandler<SettingsTreeActions['RESET_FORM']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState && formState.formType === 'edit')
    state.forms[action.payload.nodeId] = generateInitialFormState(
      action.payload.nodeId,
      formState.nodeType,
      formState.requestType,
      formState.formType,
      state.readOnly,
      formState.parentNodeId,
      formState.parentProjectId,
      formState.parentEntityId,
      formState.parentNodeType
    );
  if (formState && formState.formType === 'create') {
    delete state.forms[action.payload.nodeId];
    // Check the selected node is still the same node this action is for.
    // If it isn't, then we assume something else has been selected and we don't need to do anything.
    if (state.selectedNode?.id === action.payload.nodeId) {
      state.selectedNode = state.projectTree.nodes.find((n) => n.id === formState.parentNodeId);
    }
    state.placeholderNodes = state.placeholderNodes.filter((n) => n.id !== action.payload.nodeId);
  }
};

const onUpdateForm: SettingsTreeActionHandler<SettingsTreeActions['UPDATE_FORM']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState) {
    formState.form = action.payload.form;
    formState.isTouched = true;
  }
};

const onAcknowledgeFormSuccess: SettingsTreeActionHandler<SettingsTreeActions['ACKNOWLEDGE_FORM_SUCCESS']> = (
  state,
  action
) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState && formState.formType === 'edit') {
    formState.isSuccess = false;
  }
  if (formState && formState.formType === 'create') {
    delete state.forms[action.payload.nodeId];
    // Check the selected node is still the same node this action is for.
    // If it isn't, then we assume something else has been selected and we don't need to do anything.
    if (state.selectedNode?.id === action.payload.nodeId) {
      state.selectedNode = state.projectTree.nodes.find((n) => n.id === formState.parentNodeId);
    }
  }
};

const onSubmitForm: SettingsTreeActionHandler<SettingsTreeActions['SUBMIT_FORM']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState) {
    formState.isSubmitting = true;
    formState.error = undefined;
    formState.isSuccess = false;
  }
};

const onSubmitFormSuccess: SettingsTreeActionHandler<SettingsTreeActions['SUBMIT_FORM_SUCCESS']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState) {
    formState.isSubmitting = false;
    formState.isSuccess = true;
    formState.error = undefined;
    formState.isTouched = false;

    // If this was a create form, we need to remove the placeholder node.
    if (formState.formType === 'create') {
      state.placeholderNodes = state.placeholderNodes.filter((n) => n.id !== formState.nodeId);
    }
  }
};

const onSubmitFormError: SettingsTreeActionHandler<SettingsTreeActions['SUBMIT_FORM_ERROR']> = (state, action) => {
  const formState = state.forms[action.payload.nodeId];
  if (formState) {
    formState.isSubmitting = false;
    formState.isSuccess = false;
    if ('message' in action.payload.error) formState.error = parseErrorMessage(action.payload.error.message);
    else if (typeof action.payload.error === 'string') formState.error = action.payload.error;
    else formState.error = 'An error occurred while submitting the form. Please try again.';
  }
};

const onUpdateSettingsTree: SettingsTreeActionHandler<SettingsTreeActions['UPDATE_SETTINGS_TREE']> = (
  state,
  action
) => {
  // If there are any placeholder nodes, we need to re-add them to the tree.
  // We exclude the selected node from this list becuase if the selected node is a placeholder then
  // we have probably just created a new entity.
  // We also want to extract any nodes that came from a request to get children of a project.
  // These will not be in the updated data because they are from one or many separate requests.
  const placeHoldersNodes = state.projectTree.nodes.filter(
    (n) =>
      (n.isPlaceholder && n.id !== state.selectedNode?.id) ||
      n.request.type === SettingsTreeDataRequestType.ProjectChildren
  );
  state.projectTree = action.payload;
  if (placeHoldersNodes.length) state.projectTree.nodes.push(...placeHoldersNodes);
};

const onUpdateSettingsTreeChildren: SettingsTreeActionHandler<SettingsTreeActions['UPDATE_SETTINGS_TREE_CHILDREN']> = (
  state,
  action
) => {
  state.projectChildrenTree = action.payload;
};

export const settingsTreeActionHandlers: SettingsTreeActionHandlers = {
  selectNode: onSelectNode,
  enqueueNodeSelection: onEnqueueNodeSelection,
  clearEnqueuedNodeSelection: onClearEnqueuedNodeSelection,
  toggleNode: onToggleNode,
  createNode: onCreateNode,
  setCurrentAction: onSetCurrentAction,
  clearCurrentAction: onClearCurrentAction,
  registerForm: onRegisterForm,
  updateForm: onUpdateForm,
  resetForm: onResetForm,
  acknowledgeFormSuccess: onAcknowledgeFormSuccess,
  submitForm: onSubmitForm,
  submitFormSuccess: onSubmitFormSuccess,
  submitFormError: onSubmitFormError,
  updateSettingsTree: onUpdateSettingsTree,
  updateSettingsTreeChildren: onUpdateSettingsTreeChildren
};
